import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Response } from "../models/response";
import HttpClient from "../utils/network";
import { OnSiteApi } from "../apis/onsite";
import { CacheKey } from "../models/cacheKey";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { NetworkError } from "../models/networkError";
import { t } from "i18next";

interface UseCheckInProps {
  artistId?: number;
  eventId?: number;
  rewardId?: number;
  winnerId?: number;
  ticketCode: string | null;
}

export function useCheckIn({
  artistId,
  eventId,
  rewardId,
  winnerId,
  ticketCode,
}: UseCheckInProps) {
  const queryClient = useQueryClient();

  const { mutateAsync: checkIn } = useMutation({
    mutationFn: async ({ isTardy = false }: { isTardy?: boolean }) => {
      if (artistId && eventId && rewardId && winnerId && ticketCode) {
        try {
          const response = new Response(
            await HttpClient.request(
              OnSiteApi.checkIn(
                artistId,
                eventId,
                rewardId,
                winnerId,
                isTardy,
                ticketCode,
              ),
            ),
          );
          if (response.status < 300) {
            await queryClient.invalidateQueries({
              queryKey: [CacheKey.getUserInfo, eventId, { ticketCode }],
            });
            await queryClient.invalidateQueries({
              queryKey: [CacheKey.getCheckInStatus, eventId],
            });
          }
          return response;
        } catch (e) {
          if (e instanceof NetworkError) {
            toast.error(
              t(e.body?.tag || "", {
                n: e.body?.args[0],
              }),
            );
          }
          if (e instanceof AxiosError) {
            toast.error(e.message);
          }
        }
      }
      return null;
    },
  });
  const { mutateAsync: cancelCheckIn } = useMutation({
    mutationFn: async () => {
      if (artistId && eventId && rewardId && winnerId && ticketCode) {
        try {
          const response = new Response(
            await HttpClient.request(
              OnSiteApi.cancelCheckin(
                artistId,
                eventId,
                rewardId,
                winnerId,
                ticketCode,
              ),
            ),
          );
          if (response.status < 300) {
            await queryClient.invalidateQueries({
              queryKey: [CacheKey.getUserInfo, eventId, { ticketCode }],
            });
          }
          return response;
        } catch (e) {
          console.error(e);
          if (e instanceof AxiosError) {
            toast.error(e.message);
          }
        }
      }
      return null;
    },
  });

  return { checkIn, cancelCheckIn };
}
